/**
 * Utilitaires pour les notifications utilisateur
 * Permet d'afficher des messages temporaires sans dépendance externe
 */

export interface NotificationOptions {
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
}

/**
 * Affiche une notification simple dans le navigateur
 * Amélioration de alert() avec un message plus clair
 */
export function showNotification(options: NotificationOptions | string): void {
  const opts: NotificationOptions = typeof options === 'string' 
    ? { message: options, type: 'info' }
    : options

  const { message, type = 'info', duration = 5000 } = opts

  // Si les notifications du navigateur sont supportées, les utiliser
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(message, {
      icon: '/favicon.ico',
      tag: 'session-expired',
    })
    setTimeout(() => notification.close(), duration)
    return
  }

  // Sinon, utiliser une notification dans la page
  showToastNotification(message, type, duration)
}

/**
 * Affiche une notification toast simple dans la page
 */
function showToastNotification(message: string, type: string, duration: number): void {
  // Créer un élément de notification
  const notification = document.createElement('div')
  notification.className = `notification notification-${type}`
  notification.textContent = message

  // Styles inline pour la notification
  Object.assign(notification.style, {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: '10000',
    padding: '16px 24px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    fontSize: '14px',
    fontWeight: '500',
    animation: 'slideInRight 0.3s ease-out',
  })

  // Couleurs selon le type
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    success: { bg: '#10b981', text: '#ffffff', border: '#059669' },
    error: { bg: '#ef4444', text: '#ffffff', border: '#dc2626' },
    warning: { bg: '#f59e0b', text: '#ffffff', border: '#d97706' },
    info: { bg: '#3b82f6', text: '#ffffff', border: '#2563eb' },
  }

  const color = colors[type] || colors.info
  notification.style.backgroundColor = color.bg
  notification.style.color = color.text
  notification.style.borderLeft = `4px solid ${color.border}`

  // Ajouter l'animation CSS si elle n'existe pas
  if (!document.getElementById('notification-styles')) {
    const style = document.createElement('style')
    style.id = 'notification-styles'
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `
    document.head.appendChild(style)
  }

  // Ajouter au DOM
  document.body.appendChild(notification)

  // Retirer après la durée spécifiée
  setTimeout(() => {
    notification.style.animation = 'slideOutRight 0.3s ease-out'
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 300)
  }, duration)

  // Permettre de fermer en cliquant
  notification.addEventListener('click', () => {
    notification.style.animation = 'slideOutRight 0.3s ease-out'
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 300)
  })
}

/**
 * Demander la permission pour les notifications navigateur
 * IMPORTANT: Cette fonction DOIT être appelée depuis un gestionnaire d'événements utilisateur
 * (ex: onClick, onMouseDown) pour respecter les règles du navigateur
 * 
 * @param callback - Fonction appelée après la réponse de l'utilisateur
 */
export function requestNotificationPermission(callback?: (permission: NotificationPermission) => void): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Les notifications ne sont pas supportées par ce navigateur')
    return Promise.resolve('denied')
  }

  if (Notification.permission === 'granted') {
    console.log('Permission de notification déjà accordée')
    callback?.(Notification.permission)
    return Promise.resolve(Notification.permission)
  }

  if (Notification.permission === 'denied') {
    console.warn('Permission de notification refusée. L\'utilisateur doit l\'activer manuellement dans les paramètres du navigateur.')
    callback?.(Notification.permission)
    return Promise.resolve(Notification.permission)
  }

  // Demander la permission (doit être appelé depuis un événement utilisateur)
  return Notification.requestPermission().then((permission) => {
    if (permission === 'granted') {
      console.log('Permission de notification accordée')
    } else {
      console.log('Permission de notification refusée')
    }
    callback?.(permission)
    return permission
  })
}
